import * as React from 'react';

import {
  cn,
  Col,
  DropZone,
  type DropZoneProps,
  isFileDropItem,
  Label,
} from '@rematter/ui-web';
import { Loader2, Upload } from 'lucide-react';

import { DefaultFileCard } from './DefaultFileCard';

import { ACCEPT_EXTENSIONS } from '~/shared/constants/fileValidation';
import { useFileDownload } from '~/shared/hooks/useFileDownload';
import { useFileUpload } from '~/shared/hooks/useFileUpload';
import { useTranslations } from '~/shared/i18n/useTranslations';
import {
  FileFormData,
  FileMutations,
  GenericFile,
  UploadErrorActions,
} from '~/shared/types/file';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES & CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

type DropZoneTheme = 'default' | 'compact';

export type FilesTabProps = {
  entityId?: string;
  files: GenericFile[];
  onFilesChange?: (files: GenericFile[]) => void;
  onAllUploadsComplete?: () => void;
  mutations: FileMutations;
  isLoading?: boolean;
  isVisible?: boolean;
  title?: string;
  dropZoneText?: {
    primary: React.ReactNode;
    secondary: string;
  };
  dropZoneTheme?: DropZoneTheme;
  renderItems?: (
    files: FileFormData[],
    defaultRenderer: (files: FileFormData[]) => React.ReactNode,
    uploadErrorActions: UploadErrorActions,
  ) => React.ReactNode;
  renderItemsHeader?: (files: FileFormData[]) => React.ReactNode;
  onFileClick?: (file: GenericFile) => void;
  className?: string;
  variant?: 'field' | 'default';
  disabled?: boolean;
  required?: boolean;
  mimeType?: string;
  defaultMetadata?: Record<string, unknown>;
};

const dropZoneStyles = {
  default: {
    container:
      'h-32 w-full cursor-pointer rounded-lg border-2 border-dashed border-neutral/30 transition-colors hover:border-neutral/40',
    content: 'flex size-full flex-col items-center justify-center',
    showIcon: true,
    iconClass: 'mx-auto mb-2 size-8 opacity-40',
    primaryText: 'text-sm text-neutral/60',
    secondaryText: 'mt-1 text-xs text-neutral/50',
  },
  compact: {
    container:
      'h-20 w-full max-w-140 cursor-pointer rounded-lg border-2 border-dashed border-primary/40 bg-primary/10 transition-colors hover:border-primary/60',
    content: 'flex size-full flex-col items-center justify-center',
    showIcon: false,
    iconClass: '',
    primaryText: 'text-base text-neutral',
    secondaryText: 'mt-1 text-sm text-neutral/60',
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export const FilesTab = ({
  entityId,
  files,
  onFilesChange,
  onAllUploadsComplete,
  mutations,
  isLoading = false,
  isVisible = true,
  title,
  dropZoneText,
  dropZoneTheme = 'default',
  renderItems,
  renderItemsHeader,
  onFileClick,
  className = '',
  variant = 'default',
  disabled = false,
  required = false,
  mimeType,
  defaultMetadata,
}: FilesTabProps) => {
  const t = useTranslations();
  const { downloadFile, isGeneratingDownloadUrl } = useFileDownload();

  const {
    localFiles,
    isCreating,
    isConfirming,
    isDeleting,
    handleFileSelection,
    removeFile,
    retryUpload,
    uploadErrorActions,
  } = useFileUpload({
    entityId,
    mutations,
    files,
    onFilesChange,
    onAllUploadsComplete,
    defaultMetadata,
    mimeType,
  });

  const handleFileDrop: DropZoneProps['onDrop'] = async (e) => {
    if (disabled || !entityId) return;

    const fileItems = e.items.filter(isFileDropItem);
    const droppedFiles = await Promise.all(
      fileItems.map((item) => item.getFile()),
    );

    await handleFileSelection(droppedFiles);
  };

  const handleClickToUpload = (e: React.MouseEvent) => {
    if (disabled || !entityId) return;

    e.preventDefault();
    e.stopPropagation();

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.multiple = true;
    fileInput.accept = mimeType || ACCEPT_EXTENSIONS;
    fileInput.style.display = 'none';

    fileInput.onchange = async (event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const selectedFiles = Array.from(target.files);
        await handleFileSelection(selectedFiles);
      }
      document.body.removeChild(fileInput);
    };

    document.body.appendChild(fileInput);
    setTimeout(() => fileInput.click(), 0);
  };

  const renderDefaultFile = (file: FileFormData) => (
    <DefaultFileCard
      file={file}
      onDownload={() => downloadFile(file)}
      onDelete={() => removeFile(file.id)}
      onRetry={() => retryUpload(file.id)}
      onFileClick={onFileClick}
      isDeleting={isDeleting}
      isDownloading={isGeneratingDownloadUrl}
      isUploading={isCreating || isConfirming}
      retryLabel={t.files.retry}
      addedOnLabel={t.files.addedOn}
    />
  );

  if (!isVisible) {
    return null;
  }

  const displayDropZoneText = dropZoneText || {
    primary: t.files.dropZone.primary,
    secondary: t.files.dropZone.secondary,
  };

  const currentTheme = dropZoneStyles[dropZoneTheme];

  return (
    <div className={cn('@FilesTab flex flex-col gap-6', className)}>
      <Col gap4={variant === 'default'} gap2={variant === 'field'}>
        {title && (
          <div>
            <Label
              className={cn(
                variant === 'default' &&
                  'text-lg font-semibold text-neutral/90',
              )}
              aria-required={required}
            >
              {title}
            </Label>
          </div>
        )}

        {/* File Drop Zone */}
        {!disabled && (
          <DropZone onDrop={handleFileDrop} className={currentTheme.container}>
            <div onClick={handleClickToUpload} className={currentTheme.content}>
              {currentTheme.showIcon && (
                <Upload className={currentTheme.iconClass} />
              )}
              <p className={currentTheme.primaryText}>
                {displayDropZoneText.primary}
              </p>
              <p className={currentTheme.secondaryText}>
                {displayDropZoneText.secondary}
              </p>
            </div>
          </DropZone>
        )}

        {/* File Header */}
        {renderItemsHeader && renderItemsHeader(localFiles)}

        {/* File Items */}
        {localFiles.length > 0 && (
          <div className="space-y-3">
            {renderItems
              ? renderItems(
                  localFiles,
                  (filesToRender) => (
                    <>
                      {filesToRender.map((file) => (
                        <div key={file.id}>{renderDefaultFile(file)}</div>
                      ))}
                    </>
                  ),
                  uploadErrorActions,
                )
              : localFiles.map((file) => (
                  <div key={file.id}>{renderDefaultFile(file)}</div>
                ))}
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center p-4">
            <Loader2 className="size-6 animate-spin text-neutral/40" />
            <span className="ml-2 text-sm text-neutral/50">
              Loading files...
            </span>
          </div>
        )}
      </Col>
    </div>
  );
};
